import { useEffect, useState, useCallback, useMemo } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "../ui/dialog";
import {
  Lock, Unlock, ShieldCheck, Calendar, AlertTriangle, Loader2,
  Clock, User, CheckCircle2, XCircle,
} from "lucide-react";
import { toast } from "sonner";
import api from "../../lib/api";
import { useRestaurant } from "../../context/RestaurantContext";

const MONTHS_ES = [
  "Enero", "Febrero", "Marzo", "Abril", "Mayo", "Junio",
  "Julio", "Agosto", "Septiembre", "Octubre", "Noviembre", "Diciembre",
];

// ─── LOCK / UNLOCK DIALOG ──────────────────────────────────────────────
function LockActionDialog({ open, onClose, mode, restaurantId, defaults, onSuccess, unlockRequest }) {
  const [step, setStep] = useState("form"); // form | otp
  const [year, setYear] = useState(defaults?.year || new Date().getFullYear());
  const [month, setMonth] = useState(defaults?.month || new Date().getMonth() + 1);
  const [reason, setReason] = useState(unlockRequest?.reason || "");
  const [otp, setOtp] = useState("");
  const [otpInfo, setOtpInfo] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (open) {
      setStep("form");
      setYear(defaults?.year || new Date().getFullYear());
      setMonth(defaults?.month || new Date().getMonth() + 1);
      setReason(unlockRequest?.reason || "");
      setOtp("");
      setOtpInfo(null);
    }
  }, [open, defaults, unlockRequest]);

  const title = mode === "lock" ? "Cerrar mes fiscal" : "Reabrir mes fiscal";
  const Icon = mode === "lock" ? Lock : Unlock;

  const handleRequestOtp = useCallback(async () => {
    if (mode === "unlock" && !reason.trim()) {
      toast.error("Debes indicar un motivo para reabrir el mes");
      return;
    }
    setBusy(true);
    try {
      const fn = mode === "lock" ? api.requestLockOtp : api.requestUnlockOtp;
      const res = await fn.call(api, restaurantId, { year, month });
      setOtpInfo(res);
      setStep("otp");
      if (res?.debug_otp) {
        toast.info(`OTP de pruebas: ${res.debug_otp}`);
      } else {
        toast.success(`Código enviado a ${res?.sent_to || "tu email"}`);
      }
    } catch (e) {
      toast.error(e?.message || "No se pudo solicitar el código");
    } finally {
      setBusy(false);
    }
  }, [restaurantId, mode, year, month, reason]);

  const handleConfirm = useCallback(async () => {
    if (!otp.trim()) {
      toast.error("Introduce el código OTP");
      return;
    }
    setBusy(true);
    try {
      if (mode === "lock") {
        await api.confirmMonthlyLock(restaurantId, { year, month, otp: otp.trim() });
        toast.success(`Mes ${year}-${String(month).padStart(2, "0")} cerrado fiscalmente`);
      } else {
        await api.confirmMonthlyUnlock(restaurantId, {
          year, month, otp: otp.trim(), reason,
          requestId: unlockRequest?.request_id || null,
        });
        toast.success(`Mes ${year}-${String(month).padStart(2, "0")} reabierto`);
      }
      onSuccess?.();
      onClose();
    } catch (e) {
      toast.error(e?.message || "Error al confirmar");
    } finally {
      setBusy(false);
    }
  }, [restaurantId, mode, year, month, otp, reason, unlockRequest, onSuccess, onClose]);

  return (
    <Dialog open={open} onOpenChange={(v) => !v && !busy && onClose()}>
      <DialogContent className="max-w-md" data-testid={`monthly-${mode}-dialog`}>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Icon className={`h-5 w-5 ${mode === "lock" ? "text-rose-700" : "text-emerald-700"}`} />
            {title}
          </DialogTitle>
        </DialogHeader>
        {step === "form" ? (
          <div className="space-y-3 pt-2 text-sm">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Año</Label>
                <Input type="number" value={year} min={2020} max={2100}
                  onChange={(e) => setYear(parseInt(e.target.value, 10))} />
              </div>
              <div>
                <Label className="text-xs">Mes</Label>
                <select className="w-full h-10 rounded border px-2 text-sm bg-card"
                  value={month}
                  onChange={(e) => setMonth(parseInt(e.target.value, 10))}
                  data-testid={`monthly-${mode}-month-select`}>
                  {MONTHS_ES.map((m, i) => (
                    <option key={i + 1} value={i + 1}>{i + 1} · {m}</option>
                  ))}
                </select>
              </div>
            </div>
            {mode === "unlock" && (
              <div>
                <Label className="text-xs">Motivo de reapertura <span className="text-rose-700">*</span></Label>
                <Input
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  placeholder="Ej. Factura pendiente recibida tarde"
                  data-testid="monthly-unlock-reason"
                />
                <p className="text-[10px] text-muted-foreground mt-1">
                  Queda registrado en la auditoría. Solo tú puedes reabrir.
                </p>
              </div>
            )}
            {unlockRequest && (
              <div className="rounded bg-amber-50 border border-amber-200 p-2 text-[11px] text-amber-800">
                Resolviendo solicitud de <b>{unlockRequest.requested_by_name || unlockRequest.requested_by}</b>
              </div>
            )}
            <div className={`rounded p-2 text-[11px] border ${
              mode === "lock" ? "bg-rose-50 border-rose-200 text-rose-800" : "bg-emerald-50 border-emerald-200 text-emerald-800"
            }`}>
              {mode === "lock"
                ? "Al cerrar el mes, nadie podrá modificar ventas, gastos, inventario ni caja de ese periodo hasta que se reabra."
                : "Al reabrir, se permitirán modificaciones retroactivas en todos los módulos. La acción queda registrada."}
            </div>
          </div>
        ) : (
          <div className="space-y-3 pt-2 text-sm">
            <p className="text-muted-foreground text-[11px]">
              {otpInfo?.sent_to
                ? `Te hemos enviado un código de 6 dígitos a ${otpInfo.sent_to}. Válido 15 minutos.`
                : "Introduce el código recibido (válido 15 minutos)."}
            </p>
            <Input
              value={otp}
              onChange={(e) => setOtp(e.target.value)}
              placeholder="000000"
              maxLength={6}
              className="text-center text-2xl tracking-widest font-mono tabular-nums"
              data-testid={`monthly-${mode}-otp-input`}
              autoFocus
            />
            <Button variant="ghost" size="sm" className="w-full text-[11px]"
              onClick={handleRequestOtp} disabled={busy}>
              ¿No te llega? Reenviar código
            </Button>
          </div>
        )}
        <DialogFooter>
          <Button variant="ghost" onClick={onClose} disabled={busy}>Cancelar</Button>
          {step === "form" ? (
            <Button onClick={handleRequestOtp} disabled={busy}
              data-testid={`monthly-${mode}-request-otp`}>
              {busy ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Icon className="h-4 w-4 mr-1" />}
              Solicitar código
            </Button>
          ) : (
            <Button onClick={handleConfirm} disabled={busy || !otp.trim()}
              data-testid={`monthly-${mode}-confirm`}>
              {busy ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <ShieldCheck className="h-4 w-4 mr-1" />}
              Confirmar {mode === "lock" ? "cierre" : "reapertura"}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ─── REQUEST UNLOCK DIALOG (manager flow) ──────────────────────────────
function RequestUnlockDialog({ open, onClose, restaurantId, defaults, onSuccess }) {
  const [year, setYear] = useState(defaults?.year || new Date().getFullYear());
  const [month, setMonth] = useState(defaults?.month || new Date().getMonth() + 1);
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (open) {
      setYear(defaults?.year || new Date().getFullYear());
      setMonth(defaults?.month || new Date().getMonth() + 1);
      setReason("");
    }
  }, [open, defaults]);

  const handleSubmit = async () => {
    if (!reason.trim()) {
      toast.error("Explica el motivo para que el dueño pueda valorarla");
      return;
    }
    setBusy(true);
    try {
      await api.requestUnlockFromManager(restaurantId, { year, month, reason: reason.trim() });
      toast.success("Solicitud enviada al propietario. Recibirás una notificación con la respuesta.");
      onSuccess?.();
      onClose();
    } catch (e) {
      toast.error(e?.message || "No se pudo enviar la solicitud");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && !busy && onClose()}>
      <DialogContent className="max-w-md" data-testid="monthly-unlock-request-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <AlertTriangle className="h-5 w-5 text-amber-700" />
            Solicitar reapertura de mes
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-3 pt-2 text-sm">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">Año</Label>
              <Input type="number" value={year} min={2020} max={2100}
                onChange={(e) => setYear(parseInt(e.target.value, 10))} />
            </div>
            <div>
              <Label className="text-xs">Mes</Label>
              <select className="w-full h-10 rounded border px-2 text-sm bg-card"
                value={month}
                onChange={(e) => setMonth(parseInt(e.target.value, 10))}>
                {MONTHS_ES.map((m, i) => (
                  <option key={i + 1} value={i + 1}>{i + 1} · {m}</option>
                ))}
              </select>
            </div>
          </div>
          <div>
            <Label className="text-xs">Motivo de la solicitud <span className="text-rose-700">*</span></Label>
            <Input
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="Ej. Falta registrar factura del día 28 de Marzo"
              data-testid="monthly-unlock-request-reason"
            />
          </div>
          <div className="rounded bg-amber-50 border border-amber-200 p-2 text-[11px] text-amber-800">
            El propietario recibirá la solicitud y decidirá si la reabre. Tú no puedes hacerlo directamente.
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose} disabled={busy}>Cancelar</Button>
          <Button onClick={handleSubmit} disabled={busy || !reason.trim()}
            data-testid="monthly-unlock-request-submit">
            {busy ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <AlertTriangle className="h-4 w-4 mr-1" />}
            Enviar solicitud
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ─── MAIN CARD ─────────────────────────────────────────────────────────
export default function MonthlyLocksCard({ restaurantId, userRole }) {
  const { currentRestaurant } = useRestaurant();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [mode, setMode] = useState(null); // "lock" | "unlock" | "request"
  const [defaults, setDefaults] = useState(null);
  const [targetRequest, setTargetRequest] = useState(null);

  const isOwner = (userRole || currentRestaurant?.role) === "owner";

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.getMonthlyLocks(restaurantId);
      setData(r);
    } catch (e) {
      console.warn(e);
    } finally {
      setLoading(false);
    }
  }, [restaurantId]);

  useEffect(() => { if (restaurantId) load(); }, [restaurantId, load]);

  const activeLocks = useMemo(() => (data?.locks || []).filter(l => !l.unlocked_at), [data]);
  const historicLocks = useMemo(() => (data?.locks || []).filter(l => l.unlocked_at), [data]);

  const suggestLastMonth = () => {
    const d = new Date();
    d.setMonth(d.getMonth() - 1);
    return { year: d.getFullYear(), month: d.getMonth() + 1 };
  };

  return (
    <Card data-testid="monthly-locks-card">
      <CardHeader className="pb-3">
        <CardTitle className="text-sm flex items-center gap-2">
          <ShieldCheck className="h-4 w-4 text-violet-600" />
          Cierres fiscales mensuales
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        {loading ? (
          <div className="text-center py-4 text-[11px] text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin inline mr-1" /> Cargando…
          </div>
        ) : (
          <>
            {/* Actions */}
            <div className="flex flex-wrap gap-2">
              {isOwner ? (
                <>
                  <Button
                    size="sm" variant="default"
                    onClick={() => { setMode("lock"); setDefaults(suggestLastMonth()); }}
                    data-testid="monthly-lock-open-dialog"
                    className="gap-1"
                  >
                    <Lock className="h-3 w-3" /> Cerrar mes fiscal
                  </Button>
                  {activeLocks.length > 0 && (
                    <Button
                      size="sm" variant="outline"
                      onClick={() => {
                        setMode("unlock");
                        setDefaults({ year: activeLocks[0].year, month: activeLocks[0].month });
                        setTargetRequest(null);
                      }}
                      data-testid="monthly-unlock-open-dialog"
                      className="gap-1 text-emerald-700 border-emerald-300 hover:bg-emerald-50"
                    >
                      <Unlock className="h-3 w-3" /> Reabrir mes
                    </Button>
                  )}
                </>
              ) : (
                activeLocks.length > 0 && (
                  <Button
                    size="sm" variant="outline"
                    onClick={() => { setMode("request"); setDefaults({ year: activeLocks[0].year, month: activeLocks[0].month }); }}
                    data-testid="monthly-request-unlock-open"
                    className="gap-1 text-amber-700 border-amber-300 hover:bg-amber-50"
                  >
                    <AlertTriangle className="h-3 w-3" /> Solicitar reapertura
                  </Button>
                )
              )}
            </div>

            {/* Pending unlock requests (owner sees them) */}
            {isOwner && (data?.pending_requests || []).length > 0 && (
              <div className="border border-amber-300 bg-amber-50/50 rounded-lg p-2 space-y-2" data-testid="pending-unlock-requests">
                <div className="text-[11px] font-semibold text-amber-900 flex items-center gap-1">
                  <AlertTriangle className="h-3.5 w-3.5" />
                  {data.pending_requests.length} solicitud{data.pending_requests.length > 1 ? "es" : ""} de reapertura pendiente{data.pending_requests.length > 1 ? "s" : ""}
                </div>
                {data.pending_requests.map(req => (
                  <div key={req.request_id} className="bg-card rounded p-2 flex items-start gap-2 border border-amber-200">
                    <div className="flex-1 min-w-0">
                      <div className="text-xs font-medium">
                        {req.year}-{String(req.month).padStart(2, "0")} · {MONTHS_ES[req.month - 1]}
                      </div>
                      <div className="text-[10px] text-muted-foreground flex items-center gap-1">
                        <User className="h-2.5 w-2.5" /> {req.requested_by_name || req.requested_by}
                      </div>
                      <div className="text-[10px] text-muted-foreground italic mt-0.5">"{req.reason}"</div>
                    </div>
                    <div className="flex flex-col gap-1 shrink-0">
                      <Button
                        size="sm" className="h-6 px-2 text-[10px]"
                        onClick={() => {
                          setMode("unlock");
                          setDefaults({ year: req.year, month: req.month });
                          setTargetRequest(req);
                        }}
                        data-testid={`approve-unlock-request-${req.request_id}`}
                      >
                        <CheckCircle2 className="h-2.5 w-2.5 mr-0.5" /> Aprobar
                      </Button>
                      <Button
                        size="sm" variant="ghost" className="h-6 px-2 text-[10px] text-rose-700"
                        onClick={async () => {
                          try {
                            await api.rejectUnlockRequest(restaurantId, req.request_id);
                            toast.success("Solicitud rechazada");
                            load();
                          } catch (e) {
                            toast.error(e?.message || "Error");
                          }
                        }}
                        data-testid={`reject-unlock-request-${req.request_id}`}
                      >
                        <XCircle className="h-2.5 w-2.5 mr-0.5" /> Rechazar
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Active locks */}
            {activeLocks.length > 0 && (
              <div>
                <div className="text-[11px] uppercase tracking-wide text-muted-foreground mb-1 flex items-center gap-1">
                  <Lock className="h-3 w-3" /> Activos ({activeLocks.length})
                </div>
                <div className="space-y-1">
                  {activeLocks.map(lock => (
                    <div
                      key={lock.lock_id}
                      className="flex items-center justify-between bg-rose-50 border border-rose-200 rounded p-2"
                      data-testid={`active-lock-${lock.year}-${lock.month}`}
                    >
                      <div className="flex items-center gap-2">
                        <Calendar className="h-3.5 w-3.5 text-rose-700" />
                        <div>
                          <div className="text-xs font-semibold text-rose-900">
                            {MONTHS_ES[lock.month - 1]} {lock.year}
                          </div>
                          <div className="text-[10px] text-rose-700">
                            Bloqueado {lock.locked_method === "auto" ? "automáticamente" : "manualmente"}
                          </div>
                        </div>
                      </div>
                      <Lock className="h-3.5 w-3.5 text-rose-700" />
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* History (collapsible) */}
            {historicLocks.length > 0 && (
              <details className="text-xs">
                <summary className="cursor-pointer text-muted-foreground hover:text-gray-900">
                  Historial ({historicLocks.length})
                </summary>
                <div className="mt-2 space-y-1">
                  {historicLocks.slice(0, 10).map(lock => (
                    <div key={lock.lock_id} className="flex items-start gap-2 text-[11px] border-l-2 border-border pl-2 py-0.5">
                      <Clock className="h-3 w-3 text-muted-foreground mt-0.5 shrink-0" />
                      <div className="flex-1">
                        <div><b>{MONTHS_ES[lock.month - 1]} {lock.year}</b> · reabierto</div>
                        {lock.unlock_reason && (
                          <div className="text-muted-foreground italic">"{lock.unlock_reason}"</div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </details>
            )}

            {activeLocks.length === 0 && (!data?.pending_requests || data.pending_requests.length === 0) && historicLocks.length === 0 && (
              <div className="text-center py-3 text-[11px] text-muted-foreground">
                Ningún mes fiscal cerrado todavía. El sistema cerrará automáticamente los meses 3 días después de que finalicen.
              </div>
            )}
          </>
        )}
      </CardContent>

      {mode === "lock" && (
        <LockActionDialog
          open mode="lock" restaurantId={restaurantId}
          defaults={defaults}
          onClose={() => setMode(null)}
          onSuccess={load}
        />
      )}
      {mode === "unlock" && (
        <LockActionDialog
          open mode="unlock" restaurantId={restaurantId}
          defaults={defaults}
          unlockRequest={targetRequest}
          onClose={() => { setMode(null); setTargetRequest(null); }}
          onSuccess={load}
        />
      )}
      {mode === "request" && (
        <RequestUnlockDialog
          open restaurantId={restaurantId}
          defaults={defaults}
          onClose={() => setMode(null)}
          onSuccess={load}
        />
      )}
    </Card>
  );
}
